import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { ProcessesProps, ProcessesFilterType, ProcessType } from "../../types/process";

export interface ProcessesState {
  processes: ProcessesProps[];
  totalProcesses: number;
  filter: ProcessesFilterType;
  isProcessModalOpen: boolean;
  editProcess: ProcessesProps | null;
  loading: boolean;
  error: string | null;
}

const initialState: ProcessesState = {
  processes: [],
  totalProcesses: 0,
  filter: {
    search: null,
    type: null,
    sortBy: "id",
    sortOrder: "desc",
  },
  isProcessModalOpen: false,
  editProcess: null,
  loading: false,
  error: null,
};

type FetchProcessesListDataProps = {
  page: number;
  size?: number;
  search?: string | null;
  type?: ProcessType | null;
  sortBy?: string;
  sortOrder?: string;
};

export const fetchProcessesListData = createAsyncThunk(
  "processes/fetchList",
  async (req: FetchProcessesListDataProps) => {
    let response: any;
    await GET(`/v1/processes`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const processesSlice = createSlice({
  name: "processes",
  initialState,
  reducers: {
    toggleProcessModal: (state, action: PayloadAction<boolean>) => {
      state.isProcessModalOpen = action.payload;
    },
    setEditProcess: (state, action: PayloadAction<ProcessesProps | null>) => {
      state.editProcess = action.payload;
    },
    setProcessesFilter: (state, action: PayloadAction<Partial<ProcessesFilterType>>) => {
      state.filter = { ...state.filter, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProcessesListData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProcessesListData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.processes = action.payload.data.data.records;
        state.totalProcesses = action.payload.data.data.total_records;
        state.error = null;
      } else {
        state.processes = [];
        state.error =
          action?.payload?.data?.message ||
          "Something went wrong. Please try again.";
      }
    });
    builder.addCase(fetchProcessesListData.rejected, (state, action: any) => {
      state.processes = [];
      state.loading = false;
      state.error =
        action?.payload?.data?.message ||
        "Something went wrong. Please try again.";
    });
  },
});

export const { toggleProcessModal, setEditProcess, setProcessesFilter } = processesSlice.actions;

export const getProcesses = (state: RootState) => state.processes.processes;
export const getTotalProcesses = (state: RootState) => state.processes.totalProcesses;
export const getProcessesFilter = (state: RootState) => state.processes.filter;
export const getIsProcessModalOpen = (state: RootState) => state.processes.isProcessModalOpen;
export const getEditProcess = (state: RootState) => state.processes.editProcess;
export const getProcessesLoading = (state: RootState) => state.processes.loading;

export default processesSlice.reducer;
